import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '@/services/authApi';
import { accountApi } from '@/services/accountApi';
import { tokenStorage, ApiClientError } from '@/services/apiClient';
import { readTokenClaims } from '@/services/tokenClaims';
import type { AuthResult, User } from '@/types/auth';

// idle:          no token, or token was just cleared (logged out)
// verifying:     token present but we're still resolving /me on app load
// loading:       a login/register/google/guest thunk is in flight
// authenticated: we have a valid token AND a fetched user (real OR guest)
// error:         the last login/register/google attempt failed
export type AuthStatus = 'idle' | 'verifying' | 'loading' | 'authenticated' | 'error';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  token: string | null;
  error: string | null;
  /** Derived from JWT claims. True when user has redeemed an access code or is admin. */
  access: boolean;
  /** Derived from JWT claims. True when user's email matches ADMIN_EMAIL on the server. */
  admin: boolean;
}

// If we found a token in storage on boot, mark ourselves "verifying" rather
// than "idle" so ProtectedRoute can show a loader instead of flashing the
// protected child before /me resolves.
const bootToken = tokenStorage.get();
const bootClaims = readTokenClaims(bootToken);
const initialState: AuthState = {
  status: bootToken ? 'verifying' : 'idle',
  user: null,
  token: bootToken,
  error: null,
  access: bootClaims.access,
  admin: bootClaims.admin,
};

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

export const registerUser = createAsyncThunk<AuthResult, { email: string; password: string; displayName?: string }, { rejectValue: string }>(
  'auth/register',
  async (input, { rejectWithValue }) => {
    try {
      return await authApi.register(input);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const loginUser = createAsyncThunk<AuthResult, { email: string; password: string }, { rejectValue: string }>(
  'auth/login',
  async (input, { rejectWithValue }) => {
    try {
      return await authApi.login(input);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const loginWithGoogle = createAsyncThunk<AuthResult, string, { rejectValue: string }>(
  'auth/google',
  async (credential, { rejectWithValue }) => {
    try {
      return await authApi.google(credential);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const loginAsGuest = createAsyncThunk<AuthResult, { localId: string; displayName?: string | null }, { rejectValue: string }>(
  'auth/guest',
  async (input, { rejectWithValue }) => {
    try {
      return await authApi.guest({
        localId: input.localId,
        displayName: input.displayName ?? undefined,
      });
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const fetchCurrentUser = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/me',
  async (_, { rejectWithValue }) => {
    try {
      const { user } = await authApi.me();
      return user;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const redeemAccessCode = createAsyncThunk<{ token: string; userAccessPersisted: boolean }, string, { rejectValue: string }>(
  'auth/redeemAccessCode',
  async (code, { rejectWithValue }) => {
    try {
      const result = await authApi.redeemAccessCode(code);
      return { token: result.token, userAccessPersisted: result.userAccessPersisted };
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const updateDisplayName = createAsyncThunk<User, string, { rejectValue: string }>(
  'auth/updateDisplayName',
  async (displayName, { rejectWithValue }) => {
    try {
      const { user } = await accountApi.updateDisplayName(displayName);
      return user;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      tokenStorage.clear();
      state.status = 'idle';
      state.user = null;
      state.token = null;
      state.error = null;
      state.access = false;
      state.admin = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const applyToken = (state: AuthState, token: string): void => {
      state.token = token;
      const claims = readTokenClaims(token);
      state.access = claims.access;
      state.admin = claims.admin;
      tokenStorage.set(token);
    };

    const handleAuthSuccess = (state: AuthState, action: PayloadAction<AuthResult>): void => {
      state.status = 'authenticated';
      state.user = action.payload.user;
      state.error = null;
      applyToken(state, action.payload.token);
    };

    const handlePending = (state: AuthState): void => {
      state.status = 'loading';
      state.error = null;
    };

    const handleRejected = (state: AuthState, action: { payload?: string; error: { message?: string } }): void => {
      state.status = 'error';
      state.error = action.payload ?? action.error.message ?? 'Authentication failed';
    };

    builder
      .addCase(registerUser.pending, handlePending)
      .addCase(registerUser.fulfilled, handleAuthSuccess)
      .addCase(registerUser.rejected, handleRejected)
      .addCase(loginUser.pending, handlePending)
      .addCase(loginUser.fulfilled, handleAuthSuccess)
      .addCase(loginUser.rejected, handleRejected)
      .addCase(loginWithGoogle.pending, handlePending)
      .addCase(loginWithGoogle.fulfilled, handleAuthSuccess)
      .addCase(loginWithGoogle.rejected, handleRejected)
      .addCase(loginAsGuest.pending, handlePending)
      .addCase(loginAsGuest.fulfilled, (state, action) => {
        // The /guest endpoint omits `guest:true` from the user payload — the
        // token says guest, the user object alone doesn't. Tag it here so the
        // UI can branch on `user.guest`.
        state.status = 'authenticated';
        state.user = { ...action.payload.user, guest: true };
        state.error = null;
        applyToken(state, action.payload.token);
      })
      .addCase(loginAsGuest.rejected, handleRejected)
      .addCase(fetchCurrentUser.pending, (state) => {
        // Only override status if we don't already have a successful auth.
        // (Don't downgrade an authenticated user to "verifying" if e.g. some
        // background refresh dispatches fetchCurrentUser.)
        if (state.status !== 'authenticated') state.status = 'verifying';
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        // Token is dead/invalid — clear it and drop to logged-out. Any
        // ProtectedRoute will now redirect on its next render.
        tokenStorage.clear();
        state.status = 'idle';
        state.user = null;
        state.token = null;
        state.access = false;
        state.admin = false;
      })
      .addCase(redeemAccessCode.pending, (state) => {
        state.error = null;
      })
      .addCase(redeemAccessCode.fulfilled, (state, action) => {
        applyToken(state, action.payload.token);
      })
      .addCase(redeemAccessCode.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? 'Invalid access code';
      })
      .addCase(updateDisplayName.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(updateDisplayName.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? 'Failed to update display name';
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
